import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import { useAuth, useUser } from "@clerk/clerk-react";
import { NotificationInvitation, NotificationTask } from "../types";
import { useNavigate } from "react-router-dom";
import { useUserWebSocket } from "./useUserWebSocket";
import { Toast, ToastContainer } from "./Toasts";
import { generateId } from "../services/utils";

interface NotificationsContextType {
  hasPermission: boolean;
  requestPermission: () => Promise<void>;
  notifications: {
    invitations: NotificationInvitation[];
    tasks: NotificationTask[];
  };
}

const NotificationsContext = createContext<NotificationsContextType | null>(
  null,
);

export const useNotifications = () => {
  const context = useContext(NotificationsContext);
  if (!context) {
    throw new Error(
      "useNotifications must be used within a NotificationsProvider",
    );
  }
  return context;
};

export const NotificationsProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const { getToken, isSignedIn, isLoaded } = useAuth();
  const { user } = useUser();
  const navigate = useNavigate();
  const [hasPermission, setHasPermission] = useState(false);
  const [notifications, setNotifications] = useState<{
    invitations: NotificationInvitation[];
    tasks: NotificationTask[];
  }>({ invitations: [], tasks: [] });
  const [toasts, setToasts] = useState<Toast[]>([]);
  const [isPaused, setIsPaused] = useState(false);

  // Track known IDs to avoid re-notifying
  const knownIdsRef = useRef<Set<string>>(new Set());
  const initialLoadRef = useRef(true);

  useEffect(() => {
    if ("Notification" in window) {
      setHasPermission(Notification.permission === "granted");
    }
  }, []);

  // Central Timer logic
  useEffect(() => {
    if (isPaused || toasts.length === 0) return;

    const interval = setInterval(() => {
      setToasts((prev) => {
        const next = prev
          .map((t) => ({ ...t, remainingTime: t.remainingTime - 100 }))
          .filter((t) => t.remainingTime > 0);
        return next;
      });
    }, 100);

    return () => clearInterval(interval);
  }, [isPaused, toasts.length]);

  // Reset pause state when all toasts are gone
  useEffect(() => {
    if (toasts.length === 0 && isPaused) {
      setIsPaused(false);
    }
  }, [toasts.length, isPaused]);

  const requestPermission = async () => {
    if (!("Notification" in window)) return;
    const permission = await Notification.requestPermission();
    setHasPermission(permission === "granted");
  };

  const showToast = useCallback(
    (
      title: string,
      message: string,
      onClick?: () => void,
      type: "info" | "success" | "error" = "info",
    ) => {
      const id = generateId();
      const duration = 5000;
      setToasts((prev) => [
        ...prev,
        {
          id,
          title,
          message,
          type,
          onClick,
          duration,
          remainingTime: duration,
        },
      ]);
    },
    [],
  );

  const removeToast = useCallback((id: string) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  const sendBrowserNotification = useCallback(
    (title: string, body: string, tag: string, onClick?: () => void) => {
      if (!("Notification" in window)) return;
      if (Notification.permission === "granted") {
        try {
          // If the app is focused, we still might want to show a browser notification
          // if the user is in another tab or the window is behind something else.
          const n = new Notification(title, {
            body,
            tag,
            icon: "/favicon.svg",
            badge: "/favicon.svg",
            silent: false,
          });

          n.onclick = (e) => {
            e.preventDefault();
            window.focus();
            if (onClick) onClick();
            n.close();
          };
        } catch (error) {
          console.error("Error showing browser notification:", error);
        }
      }
    },
    [],
  );

  const handleNotificationsUpdate = useCallback(
    (data: {
      invitations: NotificationInvitation[];
      tasks: NotificationTask[];
    }) => {
      setNotifications(data);

      const knownIds = knownIdsRef.current;
      const isInitial = initialLoadRef.current;

      // Check Invitations
      data.invitations.forEach((invite: NotificationInvitation) => {
        if (!knownIds.has(invite.id)) {
          knownIds.add(invite.id);
          if (!isInitial) {
            const title = "New Invitation";
            const body = `You have been invited to join ${invite.organisationName} as ${invite.role}`;

            // In-App
            showToast(
              title,
              body,
              () => navigate(`/${invite.organisationSlug}/dashboard`),
              "info",
            );
            // Browser
            sendBrowserNotification(title, body, invite.id, () =>
              navigate(`/${invite.organisationSlug}/dashboard`),
            );
          }
        }
      });

      // Check Tasks
      data.tasks.forEach((task: NotificationTask) => {
        if (!knownIds.has(task.id)) {
          knownIds.add(task.id);
          if (!isInitial) {
            const title = "Task Ready";
            const body = `Task "${task.taskName}" in "${task.planRunName}" is waiting for you.`;
            const link = `/${task.organisationSlug}/runs/${task.planRunId}`;

            // In-App
            showToast(title, body, () => navigate(link), "success");

            // Browser
            sendBrowserNotification(title, body, task.id, () => navigate(link));
          }
        }
      });

      // Populate known IDs on initial load so we don't spam
      if (isInitial) {
        data.invitations.forEach((i) => knownIds.add(i.id));
        data.tasks.forEach((t) => knownIds.add(t.id));
        initialLoadRef.current = false;
      }
    },
    [navigate, showToast, sendBrowserNotification],
  );

  useUserWebSocket({
    user,
    onNotificationsUpdate: handleNotificationsUpdate,
  });

  return (
    <NotificationsContext.Provider
      value={{ hasPermission, requestPermission, notifications }}
    >
      {children}
      <ToastContainer
        toasts={toasts}
        onRemove={removeToast}
        onPause={setIsPaused}
        isPaused={isPaused}
      />
    </NotificationsContext.Provider>
  );
};
